import React, { useState } from "react";
import { Box, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { COLORS } from "../../constants";
import ProductImage from "./ProductImage";

const MAX_VISIBLE_THUMBNAILS = 3;

const galleryImages = (product) => {
  const media = [...(product?.media ?? [])]
    .filter((m) => !m.isDeleted && m.url)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((m) => m.url);

  return media.length > 0 ? media : [product?.image].filter(Boolean);
};

// Falls back to the plain single-image ProductImage (unchanged look/behavior) when there's nothing to slide
// between; the arrows + thumbnail strip only appear once a product actually has more than one image.
const ProductImageGallery = ({ product }) => {
  const images = galleryImages(product);
  const [activeIndex, setActiveIndex] = useState(0);

  if (images.length <= 1) {
    return <ProductImage images={images} activeIndex={0} alt={product?.name} />;
  }

  const goToPrev = () =>
    setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  const goToNext = () =>
    setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));

  const visibleThumbnails = images.slice(0, MAX_VISIBLE_THUMBNAILS);
  const hiddenCount = images.length - visibleThumbnails.length;

  return (
    <Box sx={{ width: "100%" }}>
      <ProductImage
        images={images}
        activeIndex={activeIndex}
        onActiveIndexChange={setActiveIndex}
        alt={product?.name}
      />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mt: 1.5,
        }}
      >
        <IconButton size="small" onClick={goToPrev}>
          <ChevronLeftIcon />
        </IconButton>

        <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1 }}>
          {visibleThumbnails.map((url, index) => {
            const isLastVisible = index === visibleThumbnails.length - 1;
            const showOverlay = isLastVisible && hiddenCount > 0;

            return (
              <Box
                key={url}
                onClick={() => setActiveIndex(index)}
                sx={{
                  position: "relative",
                  width: 56,
                  height: 56,
                  cursor: "pointer",
                  borderRadius: "2px",
                  backgroundColor: COLORS.white,
                  border: `2px solid ${index === activeIndex ? COLORS.themeRed : COLORS.border}`,
                }}
              >
                <Box
                  component="img"
                  src={url}
                  alt={`${product?.name ?? "Product"} thumbnail ${index + 1}`}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    p: 0.5,
                    boxSizing: "border-box",
                  }}
                />
                {showOverlay && (
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      color: COLORS.white,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  >
                    +{hiddenCount}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <IconButton size="small" onClick={goToNext}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ProductImageGallery;
